import { useState, useRef, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery } from "convex/react";
import { api } from "@constructionautomation/backend";
import { Plus, Trash2, Loader2, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import { AppLink } from "@/components/app-link";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { JobStatusBadge } from "@/components/job-status-badge";
import { EmptyJobsState } from "@/components/empty-state";
import { formatRelativeTime } from "@/lib/utils";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useInfiniteScroll } from "@/lib/use-infinite-scroll";
import { useProject } from "@/components/projects";

export const Route = createFileRoute("/specPilot/")({
  component: DashboardPage,
});

const PAGE_SIZE = 40;

function useInlineEdit(initialValue: string, onSave: (value: string) => Promise<void>) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== initialValue) {
      try {
        await onSave(trimmed);
      } catch {
        setValue(initialValue);
      }
    } else {
      setValue(initialValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    } else if (e.key === "Escape") {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  return {
    isEditing,
    setIsEditing,
    value,
    setValue,
    inputRef,
    handleSave,
    handleKeyDown,
  };
}

export function DashboardPage() {
  const navigate = useAppNavigate();
  const { projectId, project, isLoading: isProjectLoading } = useProject();
  const jobsQuery = usePaginatedQuery(
    api.specPilot.submittals.listWithTableCountsPaginated,
    projectId ? { projectId } : "skip",
    { initialNumItems: PAGE_SIZE }
  );
  const deleteJob = useMutation(api.specPilot.submittals.remove);
  const updateName = useMutation(api.specPilot.submittals.updateName);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);

  const jobs = jobsQuery.results;
  const isLoadingFirstPage = jobsQuery.status === "LoadingFirstPage" && jobs.length === 0;
  const loadMoreRef = useInfiniteScroll(
    jobsQuery.status === "CanLoadMore",
    jobsQuery.loadMore,
    PAGE_SIZE
  );

  // Show loading state while project context initializes
  if (isProjectLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="SpecPilot"
          description="Loading project..."
        />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const handleDeleteJob = async (submittalId: string) => {
    setDeletingJobId(submittalId);
    try {
      await deleteJob({
        submittalId: submittalId as Parameters<typeof deleteJob>[0]["submittalId"],
      });
      toast.success("SpecPilot job deleted successfully");
    } catch (error) {
      toast.error("Failed to delete SpecPilot job");
      console.error("Delete error:", error);
    } finally {
      setDeletingJobId(null);
    }
  };

  const handleUpdateName = async (submittalId: string, name: string) => {
    try {
      await updateName({
        submittalId: submittalId as Parameters<typeof updateName>[0]["submittalId"],
        name,
      });
      toast.success("Name updated");
    } catch (error) {
      toast.error("Failed to update name");
      console.error(error);
      throw error;
    }
  };

  const handleNewSpecPilot = () => {
    navigate({ to: "/specPilot/new", title: "New SpecPilot" });
  };

  if (isLoadingFirstPage) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="SpecPilot"
          description={project?.name ?? "Loading..."}
          actions={
            <Button onClick={handleNewSpecPilot}>
              <Plus className="size-4" />
              New SpecPilot Job
            </Button>
          }
        />
        <div className="flex items-center justify-center p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="SpecPilot"
        description={project?.name ?? "Select a project"}
        actions={
          <Button onClick={handleNewSpecPilot}>
            <Plus className="size-4" />
            New SpecPilot Job
          </Button>
        }
      />

      {jobs.length === 0 ? (
        <EmptyJobsState onCreateNew={handleNewSpecPilot} />
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[70px]">
                    <span className="sr-only">View</span>
                  </TableHead>
                  <TableHead>SpecPilot Job Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Tables Found</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-[70px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <JobRow
                    key={job._id}
                    job={job}
                    isDeleting={deletingJobId === job._id}
                    onDelete={handleDeleteJob}
                    onUpdateName={handleUpdateName}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center">
            {jobsQuery.status === "LoadingMore" && (
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface JobRowProps {
  job: {
    _id: string;
    name: string;
    status: "pending" | "processing" | "ready_for_review" | "completed" | "failed";
    tableCount?: number;
    createdAt: number;
  };
  isDeleting: boolean;
  onDelete: (submittalId: string) => Promise<void>;
  onUpdateName: (submittalId: string, name: string) => Promise<void>;
}

function JobRow({ job, isDeleting, onDelete, onUpdateName }: JobRowProps) {
  const inlineEdit = useInlineEdit(job.name, (name) => onUpdateName(job._id, name));

  return (
    <TableRow className="group">
      <TableCell>
        <AppLink
          to="/specPilot/$jobId"
          params={{ jobId: job._id }}
          title={job.name}
        >
          <Button variant="ghost" size="icon-sm" className="h-8 w-8">
            <Eye className="size-4" />
            <span className="sr-only">View {job.name}</span>
          </Button>
        </AppLink>
      </TableCell>
      <TableCell className="font-medium">
        {inlineEdit.isEditing ? (
          <Input
            ref={inlineEdit.inputRef}
            value={inlineEdit.value}
            onChange={(e) => inlineEdit.setValue(e.target.value)}
            onBlur={() => void inlineEdit.handleSave()}
            onKeyDown={inlineEdit.handleKeyDown}
            className="h-8 w-full max-w-xs"
          />
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-foreground">{job.name}</span>
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 focus:opacity-100"
              onClick={() => inlineEdit.setIsEditing(true)}
            >
              <Pencil className="size-3" />
              <span className="sr-only">Edit name</span>
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        <JobStatusBadge status={job.status} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {job.tableCount}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatRelativeTime(job.createdAt)}
      </TableCell>
      <TableCell>
        <DeleteConfirmationDialog
          title="Delete SpecPilot job"
          description={`Are you sure you want to delete "${job.name}"? This will permanently delete all extracted tables and generated submittals associated with this job.`}
          onConfirm={() => onDelete(job._id)}
        >
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-destructive"
            disabled={isDeleting}
            aria-label={`Delete ${job.name}`}
          >
            {isDeleting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
          </Button>
        </DeleteConfirmationDialog>
      </TableCell>
    </TableRow>
  );
}
